import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import Folded2025Customers from '~components/business-owner/Folded2025/components/Folded2025Customers';
import Folded2025FeatureFacts from '~components/business-owner/Folded2025/components/Folded2025FeatureFacts/Folded2025FeatureFacts';
import Folded2025Finale from '~components/business-owner/Folded2025/components/Folded2025Finale';
import Folded2025Intro from '~components/business-owner/Folded2025/components/Folded2025Intro';
import Folded2025Machines from '~components/business-owner/Folded2025/components/Folded2025Machines';
import Folded2025PickupAndDelivery from '~components/business-owner/Folded2025/components/Folded2025PickupAndDelivery';
import Folded2025Revenue from '~components/business-owner/Folded2025/components/Folded2025Revenue';
import Folded2025Team from '~components/business-owner/Folded2025/components/Folded2025Team';
import {
	useFolded2025Data,
	useFolded2025FeatureData,
} from '~components/business-owner/Folded2025/hooks/useFolded2025Data';

import { ArrowLeft, ArrowRight, XBoldIcon } from '~assets/images';
import { FOLDED_2025_TRACKING_EVENTS } from '~constants/fullstory/events/folded';
import { GlobalState } from '~reducers';
import FullStory from '~third-party/fullstory';

import styles from './Folded2025.module.scss';

const FOLDED_2025_MOBILE_WIDTH = 576;

const Folded2025 = (): JSX.Element => {
	const location = useLocation();
	const navigate = useNavigate();
	const { data } = useFolded2025Data();
	const { isLoading } = useFolded2025Data();
	const { userId, email, business } = useSelector(
		(state: GlobalState) => state?.session,
	);

	const isPUDVisible =
		(data?.summary?.pickup_delivery?.total_orders_delivered || 0) > 0;

	const isMachinesVisible = (data?.summary?.machines?.total_turns || 0) > 0;

	const {
		refetch,
		isFetched: isFeatureFetched,
		data: featureFactsData,
	} = useFolded2025FeatureData();
	const isFeatureFactsVisible =
		(featureFactsData?.accelerate?.metrics?.totalFiveStarReviews || 0) > 0 ||
		(featureFactsData?.messenger?.metrics?.totalSentMessages || 0) > 0 ||
		(featureFactsData?.accelerate?.metrics?.totalCampaigns || 0) > 0 ||
		(featureFactsData?.assist?.totalDurationMilliseconds || 0) > 0;

	const ROUTE_ORDER = [
		'intro',
		'revenue',
		'customers',
		'team',
		...(isMachinesVisible ? ['machines'] : []),
		...(isFeatureFactsVisible ? ['feature-facts'] : []),
		...(isPUDVisible ? ['pickup-and-delivery'] : []),
		'finale',
	];

	// This pre-loads the feature facts data in the background so users don't see loader when they visit page
	useEffect(() => {
		if (!isFeatureFetched) {
			refetch();
		}
	}, [isFeatureFetched, refetch]);

	const [slideDirection, setSlideDirection] = useState<
		'left' | 'right' | 'fadeIn' | 'fadeOut'
	>('fadeIn');
	const previousIndexRef = useRef<number>(-1);
	const hasVisitedRef = useRef<boolean>(false);
	const isNavigatingAwayRef = useRef<boolean>(false);
	const touchStartX = useRef<number>(0);
	const touchEndX = useRef<number>(0);

	const currentRoute = useMemo(() => {
		const pathParts = location.pathname.split('/');
		return pathParts[pathParts.length - 1] || 'intro';
	}, [location.pathname]);

	const currentIndex = ROUTE_ORDER.indexOf(currentRoute);
	const isPreviousVisible = currentIndex > 0;
	const isNextVisible = currentIndex < ROUTE_ORDER.length - 1;

	// Map navigation to FullStory events
	useEffect(() => {
		const ROUTE_TO_EVENT_MAPPING = {
			'intro': FOLDED_2025_TRACKING_EVENTS.viewedIntroScreen,
			'revenue': FOLDED_2025_TRACKING_EVENTS.viewedRevenueScreen,
			'customers': FOLDED_2025_TRACKING_EVENTS.viewedCustomersScreen,
			'team': FOLDED_2025_TRACKING_EVENTS.viewedTeamScreen,
			'machines': FOLDED_2025_TRACKING_EVENTS.viewedMachinesScreen,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'feature-facts': FOLDED_2025_TRACKING_EVENTS.viewedFeatureFactsScreen,
			// eslint-disable-next-line @typescript-eslint/naming-convention
			'pickup-and-delivery': FOLDED_2025_TRACKING_EVENTS.viewedFeatureFactsScreen,
			'finale': FOLDED_2025_TRACKING_EVENTS.viewedFinaleScreen,
		};
		const eventName =
			ROUTE_TO_EVENT_MAPPING[currentRoute as keyof typeof ROUTE_TO_EVENT_MAPPING];
		if (eventName) {
			FullStory.event(eventName, {
				userId,
				email,
				business,
			});
		}
	}, [currentRoute, userId, email, business]);

	useEffect(() => {
		if (isLoading && currentRoute !== 'intro') {
			navigate('/folded/intro', { replace: true });
		}
	}, [isLoading, currentRoute, navigate]);

	useEffect(() => {
		if (currentRoute === 'intro' && !hasVisitedRef.current) {
			setSlideDirection('fadeIn');
			hasVisitedRef.current = true;
		} else if (currentIndex > previousIndexRef.current) {
			setSlideDirection('right');
		} else if (currentIndex < previousIndexRef.current) {
			setSlideDirection('left');
		}
		previousIndexRef.current = currentIndex;
	}, [currentIndex, currentRoute]);

	const handlePrevious = () => {
		if (isLoading) {
			return;
		}

		if (currentIndex > 0) {
			setSlideDirection('left');
			navigate(`/folded/${ROUTE_ORDER[currentIndex - 1]}`);
		}
	};

	const handleNext = () => {
		if (isLoading) {
			return;
		}
		if (currentIndex < ROUTE_ORDER.length - 1) {
			setSlideDirection('right');
			navigate(`/folded/${ROUTE_ORDER[currentIndex + 1]}`);
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (isLoading) {
				return;
			}
			if (event.key === 'ArrowLeft') {
				handlePrevious();
			} else if (event.key === 'ArrowRight') {
				handleNext();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [currentIndex, isLoading]);

	const handleTouchStart = (e: React.TouchEvent) => {
		if (isLoading || window.innerWidth > FOLDED_2025_MOBILE_WIDTH) {
			return;
		}
		const touchY = e.touches[0].clientY;
		// This makes it so when users click "X" they don't trigger swipe right
		// eslint-disable-next-line no-magic-numbers
		if (touchY <= 75) {
			return;
		}
		// Prevent swipe navigation when touching near the bottom (e.g., folded bottom bar)
		// eslint-disable-next-line no-magic-numbers
		if (touchY >= window.innerHeight - 75) {
			return;
		}
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		if (isLoading || window.innerWidth > FOLDED_2025_MOBILE_WIDTH) {
			return;
		}
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		if (isLoading || window.innerWidth > FOLDED_2025_MOBILE_WIDTH) {
			return;
		}

		if (touchStartX.current === 0) {
			return;
		}

		// eslint-disable-next-line no-magic-numbers
		const swipeThreshold = 50;
		const swipeDistance = touchStartX.current - touchEndX.current;

		if (Math.abs(swipeDistance) > swipeThreshold) {
			if (swipeDistance > 0) {
				handleNext();
			} else {
				handlePrevious();
			}
		}

		touchStartX.current = 0;
		touchEndX.current = 0;
	};

	const getTransitionClassNames = () => {
		if (slideDirection === 'right') {
			return {
				enter: styles.slideRightEnter,
				enterActive: styles.slideRightEnterActive,
				exit: styles.slideRightExit,
				exitActive: styles.slideRightExitActive,
			};
		}

		if (slideDirection === 'left') {
			return {
				enter: styles.slideLeftEnter,
				enterActive: styles.slideLeftEnterActive,
				exit: styles.slideLeftExit,
				exitActive: styles.slideLeftExitActive,
			};
		}

		return {
			enter: styles.fadeCenterEnter,
			enterActive: styles.fadeCenterEnterActive,
			exit: styles.fadeCenterExit,
			exitActive: styles.fadeCenterExitActive,
		};
	};

	const navigateAway = (path: string) => {
		isNavigatingAwayRef.current = true;
		navigate(path);
	};

	return (
		<div
			className={styles.container}
			onTouchStart={handleTouchStart}
			onTouchMove={handleTouchMove}
			onTouchEnd={handleTouchEnd}
		>
			<div
				className={styles.closeButton}
				onClick={() => navigateAway('/dashboard/analytics-dashboard')}
				aria-label="Close"
			>
				<XBoldIcon />
			</div>

			{isPreviousVisible && (
				<div
					className={styles.navigationArrowLeft}
					onClick={handlePrevious}
					aria-label="Previous page"
				>
					<ArrowLeft />
				</div>
			)}

			{isNextVisible && (
				<div
					className={styles.navigationArrowRight}
					onClick={handleNext}
					aria-label="Next page"
				>
					<ArrowRight />
				</div>
			)}

			<TransitionGroup className={styles.transitionGroup}>
				<CSSTransition
					key={location.pathname}
					timeout={500}
					classNames={getTransitionClassNames()}
				>
					<div className={styles.routesWrapper}>
						<Routes location={location}>
							<Route
								path="intro"
								element={
									<Folded2025Intro
										startExperience={() => {
											handleNext();
										}}
									/>
								}
							/>
							<Route
								path="customers"
								element={
									isLoading ? (
										<Navigate to="/folded/intro" replace />
									) : (
										<Folded2025Customers />
									)
								}
							/>
							<Route
								path="revenue"
								element={
									isLoading ? (
										<Navigate to="/folded/intro" replace />
									) : (
										<Folded2025Revenue />
									)
								}
							/>
							<Route
								path="team"
								element={
									isLoading ? (
										<Navigate to="/folded/intro" replace />
									) : (
										<Folded2025Team />
									)
								}
							/>
							{isMachinesVisible && (
								<Route
									path="machines"
									element={
										isLoading ? (
											<Navigate to="/folded/intro" replace />
										) : (
											<Folded2025Machines />
										)
									}
								/>
							)}
							<Route
								path="feature-facts"
								element={
									isLoading ? (
										<Navigate to="/folded/intro" replace />
									) : (
										<Folded2025FeatureFacts />
									)
								}
							/>
							{isPUDVisible && (
								<Route
									path="pickup-and-delivery"
									element={
										isLoading ? (
											<Navigate to="/folded/intro" replace />
										) : (
											<Folded2025PickupAndDelivery />
										)
									}
								/>
							)}
							<Route
								path="finale"
								element={
									isLoading ? (
										<Navigate to="/folded/intro" replace />
									) : (
										<Folded2025Finale
											onNavigateAway={() =>
												navigateAway(
													'/dashboard/analytics-dashboard',
												)
											}
										/>
									)
								}
							/>
							<Route path="/" element={<Navigate to="intro" replace />} />
						</Routes>
					</div>
				</CSSTransition>
			</TransitionGroup>
		</div>
	);
};

export default Folded2025;
